import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { KeycloakAdminService } from '../keycloak/keycloak.service';
import { UserProfileService } from '../user/user-profile.service';
import { environment } from '../../../environments/environment';

export interface IMessage {
  from: string;
  destination: string;
  content: string;
  countryCode: string;
  languageCode: string;
  uuid: string;
  createdBy: string;
}

@Injectable({
  providedIn: 'root',
})
export class MessageTemplate {
  userService = inject(UserProfileService);

  get organizationId() {
    return this.userService.organizationId;
  }

  private baseUrl = `${environment.BASE_URL}/blackkonnectcxpengagement/api/`;
  private template = new BehaviorSubject<IMessage[]>([]);
  private detailedMessage = new BehaviorSubject<IMessage | null>(null);

  public loading = new BehaviorSubject<boolean>(false);

  public pagination = new BehaviorSubject<any | null>(null);

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakAdminService
  ) {}

  private get headers(): HttpHeaders {
    const token = this.keycloakService.getKcAccessToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  fetchTemplates(page: number = 0, size: number = 10): void {
    this.loading.next(true);
    this.http
      .get<any>(
        `${this.baseUrl}/message-templates/organizations/${this.organizationId}`,
        {
          headers: this.headers,
          params: {
            page: page.toString(),
            size: size.toString(),
          },
        }
      )
      .pipe(catchError(this.handleError))
      .subscribe({
        next: (data: any) => {
          const { content } = data;
          this.template.next(content);
          this.pagination.next(data);
          this.loading.next(false);
        },
        error: () => {
          this.loading.next(false);
        },
      });
  }

  createMessage(message: any): Observable<IMessage> {
    this.loading.next(true);

    return this.http
      .post<IMessage>(
        `${this.baseUrl}/message-templates/${this.organizationId}`,
        message,
        { headers: this.headers }
      )
      .pipe(
        tap(() => {
          this.fetchTemplates();
        }),
        finalize(() => this.loading.next(false))
      );
  }

  deleteTemplate(id: string): Observable<void> {
    this.loading.next(true);

    return this.http
      .delete<void>(`${this.baseUrl}engagement-templates/${id}`, {
        headers: this.headers,
      })
      .pipe(
        tap(() => {
          this.template.next(
            this.template.getValue().filter((temp) => temp.uuid !== id)
          );
          this.fetchTemplates();
        }),
        finalize(() => this.loading.next(false)),
        catchError(this.handleError)
      );
  }

  fetchDetailedMessage(id: string): void {
    this.http
      .get<IMessage>(`${this.baseUrl}/messages/${id}`, {
        headers: this.headers,
      })
      .pipe(catchError(this.handleError))
      .subscribe((data) => {
        this.detailedMessage.next(data);
      });
  }

  fetchTemplateDetail(id: string | number): Observable<any> {
    this.loading.next(true);
    return this.http
      .get<any>(
        `${this.baseUrl}v1/engagement-templates/organisations/${this.organizationId}/${id}`,
        { headers: this.headers }
      )
      .pipe(
        tap(() => this.loading.next(false)),
        catchError((err) => {
          this.loading.next(false);
          return this.handleError(err);
        })
      );
  }

  editTemplate(id: string | number, data: any): Observable<any> {
    this.loading.next(true);
    return this.http
      .put<any>(
        `${this.baseUrl}v1/engagement-templates/organisations/${this.organizationId}/${id}`,
        data,
        { headers: this.headers }
      )
      .pipe(
        tap(() => this.loading.next(false)),
        catchError((err) => {
          this.loading.next(false);
          return this.handleError(err);
        })
      );
  }

  get getTemplates(): Observable<IMessage[]> {
    return this.template.asObservable();
  }

  get getDetailedMessage(): Observable<IMessage | null> {
    return this.detailedMessage.asObservable();
  }

  private handleError(err: any): Observable<never> {
    return throwError(
      () => new Error(err.message || 'An unknown error occurred')
    );
  }
}
